/**
 * The bottom sheet (docs/design/visual-overhaul.md §12): what a dialog, a
 * picker or a list of actions is on a phone. It rises from the bottom over
 * the scrim, keeps above the home indicator (safe area) and the on-screen
 * keyboard (--vv-h), holds one scroller (its body), and closes by its close
 * button, a tap on the scrim, Escape, Back (the back stack) or a drag down
 * on its handle. `full` makes it the whole screen (a large dialog).
 *
 * Self-contained: it renders into #nx-portal and handles its own Escape,
 * so the app and the Console can both use it.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as RPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { pushBack } from './back';

export interface SheetProps {
  /** The heading; without one, give `label`. */
  title?: ReactNode;
  label?: string;
  onClose: () => void;
  children: ReactNode;
  /** A footer that stays put under the scrolling body (the sheet's actions). */
  actions?: ReactNode;
  /** The whole screen instead of a sheet from the bottom. */
  full?: boolean;
  cls?: string;
  /** False: only its own buttons close it (no scrim, Escape, Back or drag). */
  dismissable?: boolean;
  /** The close button's name (the app's own words). */
  closeLabel?: string;
}

/** How far a drag on the handle must pull the sheet down to close it. */
export const SHEET_DRAG_CLOSE = 80;
const EXIT_MS = 160;

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

export function Sheet({
  title,
  label,
  onClose,
  children,
  actions,
  full,
  cls,
  dismissable = true,
  closeLabel = 'Close',
}: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [closing, setClosing] = useState(false);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });
  // the exit plays, then the owner unmounts it
  const closingRef = useRef(false);
  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    const reduce = document.documentElement.dataset.motion === 'reduce';
    setTimeout(() => closeRef.current(), reduce ? 0 : EXIT_MS);
  }, []);
  useEffect(() => {
    const restore = document.activeElement as HTMLElement | null;
    const pop = dismissable ? pushBack(requestClose) : () => {};
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      // its content took the focus as it opened (a profile's note after "Add note"): it stays
      if (el.contains(document.activeElement) && document.activeElement !== el) return;
      (el.querySelector<HTMLElement>('[data-autofocus]') ?? el).focus({ preventScroll: true });
    });
    return () => {
      pop();
      if (restore && document.contains(restore)) restore.focus({ preventScroll: true });
    };
  }, [dismissable, requestClose]);
  // a drag down on the handle or the head
  const drag = useRef<{ id: number; y: number; dy: number } | null>(null);
  const onDown = (e: RPointerEvent<HTMLElement>) => {
    if (!dismissable || e.pointerType === 'mouse') return;
    if ((e.target as Element).closest('button, a, input')) return;
    drag.current = { id: e.pointerId, y: e.clientY, dy: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: RPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId || !ref.current) return;
    d.dy = Math.max(0, e.clientY - d.y);
    ref.current.style.transform = d.dy ? `translateY(${Math.round(d.dy)}px)` : '';
  };
  const onUp = (e: RPointerEvent<HTMLElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.id !== e.pointerId || !ref.current) return;
    if (d.dy >= SHEET_DRAG_CLOSE) requestClose();
    else ref.current.style.transform = '';
  };
  const portal =
    (typeof document !== 'undefined' && document.getElementById('nx-portal')) || document.body;
  return createPortal(
    <div
      className={'nx-sheet-layer' + (full ? ' is-full' : '')}
      data-state={closing ? 'closed' : 'open'}
    >
      <div className="nx-scrim" onClick={() => dismissable && requestClose()} />
      <div
        ref={ref}
        className={'nx-sheet' + (cls ? ' ' + cls : '')}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        {...(title ? { 'aria-labelledby': titleId } : { 'aria-label': label })}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && dismissable) {
            e.preventDefault();
            e.stopPropagation();
            requestClose();
            return;
          }
          if (e.key !== 'Tab') return;
          const list = [...e.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE)];
          if (!list.length) return;
          const first = list[0]!;
          const last = list[list.length - 1]!;
          if (
            e.shiftKey &&
            (document.activeElement === first || document.activeElement === e.currentTarget)
          ) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        <div
          className="nx-sheet-grip"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          {full ? null : <div className="handle" aria-hidden="true" />}
          {title ? (
            <div className="nx-sheet-head">
              <h2 id={titleId}>{title}</h2>
              {dismissable ? (
                <button
                  type="button"
                  className="nx-sheet-x"
                  aria-label={closeLabel}
                  onClick={() => requestClose()}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="nx-sheet-body">{children}</div>
        {actions ? <div className="nx-sheet-foot nx-actions">{actions}</div> : null}
      </div>
    </div>,
    portal,
  );
}
